import React, { useEffect, useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import Icon from '../AppIcon';
import Button from '../ui/Button';
import Input from '../ui/Input';
import Modal from '../ui/Modal';
import { useCompany } from '../context/CompanyContext';
import { useRequestBuilder } from '../context/RequestBuilderContext';
import repositoryContainer from '../../services/RepositoryContainer';
import RequestItemGroups from './RequestItemGroups';
import { formatRequestAmount } from './requestBuilderUtils';
import { getDisplayedProcessingFee } from '../../utils/feeDisplayUtils';
import { storeChatAccessToken } from '../../utils/chatAccess';
import { hasRangePricedItems, needsPriceFinalization } from '../../utils/pricingUtils';

const getLocalDate = () => {
  const now = new Date();
  const offset = now.getTimezoneOffset() * 60 * 1000;
  return new Date(now.getTime() - offset).toISOString().slice(0, 10);
};

const getErrorMessage = (error) => {
  const validationErrors = error?.data?.errors;
  if (validationErrors && typeof validationErrors === 'object') {
    const firstError = Object.values(validationErrors).flat()[0];
    if (firstError) return firstError;
  }
  return error?.data?.message || (typeof error?.data === 'string' ? error.data : null) || error?.message
    || 'We could not submit your request. Please try again.';
};

const INITIAL_FORM = {
  requestedDate: '',
  requestedTime: '',
  clientName: '',
  clientEmail: '',
  clientPhone: '',
  note: '',
};

const pendingRequestKey = (companyPath) => `clawfe:pendingRequest:${companyPath || 'global'}`;

const RequestFormModal = () => {
  const navigate = useNavigate();
  const { company, companyPath } = useCompany();
  const {
    items,
    subtotal,
    isFormOpen,
    closeForm,
    clearAll,
  } = useRequestBuilder();
  const [form, setForm] = useState(INITIAL_FORM);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState(null);
  const [copied, setCopied] = useState(false);
  const [isStartingPayment, setIsStartingPayment] = useState(false);
  const [paymentError, setPaymentError] = useState('');
  const [paymentQuote, setPaymentQuote] = useState(null);
  const [isQuoteLoading, setIsQuoteLoading] = useState(false);
  const minDate = useMemo(getLocalDate, []);
  const hasServices = useMemo(
    () => items.some((item) => item.type === 'service'),
    [items]
  );
  const hasRangePricing = useMemo(() => hasRangePricedItems(items), [items]);
  const shouldCollectPayment = Boolean(
    result && company?.requiresPayment && !needsPriceFinalization(result) && !hasRangePricing
  );

  useEffect(() => {
    if (!isFormOpen || !company?.requiresPayment || !companyPath || subtotal <= 0 || hasRangePricing) {
      setPaymentQuote(null);
      setIsQuoteLoading(false);
      return undefined;
    }

    let active = true;
    setIsQuoteLoading(true);
    const timer = window.setTimeout(async () => {
      try {
        const raw = await repositoryContainer.order.calculateFees(subtotal, companyPath);
        if (active) setPaymentQuote(raw?.response ?? raw);
      } catch {
        if (active) setPaymentQuote(null);
      } finally {
        if (active) setIsQuoteLoading(false);
      }
    }, 300);

    return () => {
      active = false;
      window.clearTimeout(timer);
    };
  }, [company?.requiresPayment, companyPath, hasRangePricing, isFormOpen, subtotal]);

  const updateField = (event) => {
    const { name, value } = event.target;
    setForm((current) => ({ ...current, [name]: value }));
    if (error) setError('');
  };

  const handleClose = () => {
    if (isSubmitting || isStartingPayment) return;
    if (result) {
      clearAll();
      setResult(null);
      setCopied(false);
      setError('');
      setPaymentError('');
      setForm(INITIAL_FORM);
    }
    closeForm();
  };

  const savePendingRequest = (request, paymentReference = null) => {
    localStorage.setItem(pendingRequestKey(companyPath), JSON.stringify({
      requestId: request.id,
      referenceCode: request.referenceCode,
      clientEmail: form.clientEmail.trim(),
      paymentReference,
    }));
  };

  const initiateRequestPayment = async (request) => {
    if (!request?.id) throw new Error('The request was created without a payment identifier.');
    if (needsPriceFinalization(request) || hasRangePricing) return;
    setIsStartingPayment(true);
    setPaymentError('');
    try {
      savePendingRequest(request);
      const callbackUrl = `${window.location.origin}/${companyPath}/request-callback`;
      const raw = await repositoryContainer.bookingRequest.initiatePayment(request.id, callbackUrl);
      const paymentData = raw?.response ?? raw;
      if (!paymentData?.authorizationUrl) throw new Error('The payment provider did not return a checkout link.');
      savePendingRequest({
        ...request,
        referenceCode: paymentData.sourceReferenceCode || request.referenceCode,
      }, paymentData.reference);
      window.location.assign(paymentData.authorizationUrl);
    } catch (paymentStartError) {
      setPaymentError(getErrorMessage(paymentStartError).replace('submit your request', 'start payment'));
    } finally {
      setIsStartingPayment(false);
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!company?.id || items.length === 0) {
      setError('The request is missing its business or item details. Please reopen the storefront and try again.');
      return;
    }

    setIsSubmitting(true);
    setError('');
    try {
      const raw = await repositoryContainer.bookingRequest.create({
        clientName: form.clientName.trim(),
        clientEmail: form.clientEmail.trim(),
        clientPhone: form.clientPhone.trim(),
        requestedDate: hasServices ? form.requestedDate : null,
        requestedTime: hasServices ? form.requestedTime : null,
        companyId: company.id,
        items: items.map((item) => ({
          serviceItemId: item.id,
          quantity: item.type === 'service' ? 1 : item.quantity,
        })),
        note: form.note.trim() || null,
      });
      const response = raw?.response ?? raw;
      if (!response?.referenceCode) throw new Error('The request was created without a reference code.');
      storeChatAccessToken(
        'bookingRequest',
        response.referenceCode,
        response.chatAccessToken
      );
      setResult(response);
      if (company?.requiresPayment && !needsPriceFinalization(response) && !hasRangePricing) {
        await initiateRequestPayment(response);
      }
    } catch (submitError) {
      setError(getErrorMessage(submitError));
    } finally {
      setIsSubmitting(false);
    }
  };

  const copyReference = async () => {
    if (!result?.referenceCode) return;
    try {
      await navigator.clipboard.writeText(result.referenceCode);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1500);
    } catch {
      setCopied(false);
    }
  };

  const trackRequest = () => {
    const referenceCode = result?.referenceCode;
    if (!referenceCode) return;
    clearAll();
    closeForm();
    navigate(`/track/${encodeURIComponent(referenceCode)}`);
  };

  return (
    <Modal
      isOpen={isFormOpen}
      onClose={handleClose}
      title={shouldCollectPayment ? 'Complete Payment' : result ? 'Request Submitted' : 'Review Your Request'}
      maxWidth="max-w-3xl"
    >
      {shouldCollectPayment ? (
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          className="py-6 text-center"
        >
          <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-primary/10 text-primary">
            <Icon name={isStartingPayment ? 'Loader2' : 'WalletCards'} size={32} className={isStartingPayment ? 'animate-spin' : ''} />
          </div>
          <h3 className="mt-5 font-heading text-2xl font-bold text-foreground">
            {isStartingPayment ? 'Opening secure payment' : 'Complete your payment'}
          </h3>
          <p className="mx-auto mt-2 max-w-md text-sm text-muted-foreground">
            Your request has been saved. Payment is required by {company?.name || 'this business'} to complete the submission.
          </p>

          <div className="mx-auto mt-6 max-w-md rounded-xl bg-muted/30 p-4">
            <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Request reference</p>
            <p className="mt-2 font-mono text-xl font-bold text-primary">{result.referenceCode}</p>
          </div>

          {paymentError && (
            <div role="alert" className="mx-auto mt-4 max-w-md rounded-lg border border-destructive/30 bg-destructive/10 p-3 text-sm text-destructive">
              {paymentError}
            </div>
          )}

          <div className="mx-auto mt-6 flex max-w-md flex-col-reverse gap-2 sm:flex-row">
            <Button type="button" variant="outline" fullWidth onClick={trackRequest} disabled={isStartingPayment}>
              Track Request
            </Button>
            <Button
              type="button"
              fullWidth
              iconName="CreditCard"
              onClick={() => initiateRequestPayment(result)}
              loading={isStartingPayment}
            >
              {paymentError ? 'Retry Payment' : 'Continue to Payment'}
            </Button>
          </div>
        </motion.div>
      ) : result ? (
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          className="py-6 text-center"
        >
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ type: 'spring', delay: 0.1 }}
            className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-success/15 text-success"
          >
            <Icon name="Check" size={34} />
          </motion.div>
          <h3 className="mt-5 font-heading text-2xl font-bold text-foreground">Request received</h3>
          <p className="mx-auto mt-2 max-w-md text-sm text-muted-foreground">
            {needsPriceFinalization(result) || hasRangePricing
              ? `${company?.name || 'The business'} will confirm the final price before payment is available.`
              : `${company?.name || 'The business'} can now review the combination, confirm availability, and respond to your request.`}
          </p>

          <div className="mx-auto mt-6 max-w-md rounded-xl border border-border bg-muted/30 p-4">
            <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Reference code</p>
            <button
              type="button"
              onClick={copyReference}
              className="mt-2 inline-flex items-center gap-2 rounded-lg px-3 py-2 font-mono text-xl font-bold text-primary transition-colors hover:bg-primary/10"
              aria-label="Copy request reference code"
            >
              {result.referenceCode}
              <Icon name={copied ? 'Check' : 'Copy'} size={17} />
            </button>
            <p className="mt-1 text-xs text-muted-foreground">
              {copied ? 'Copied to clipboard' : 'Keep this code to track your request'}
            </p>
          </div>

          <div className="mx-auto mt-6 flex max-w-md flex-col gap-2 sm:flex-row">
            <Button type="button" variant="outline" fullWidth onClick={handleClose}>
              Done
            </Button>
            <Button type="button" fullWidth onClick={trackRequest} iconName="ArrowRight" iconPosition="right">
              Track Request
            </Button>
          </div>
        </motion.div>
      ) : (
        <form onSubmit={handleSubmit} className="space-y-7">
          <section>
            <div className="mb-3 flex items-center justify-between gap-3">
              <h3 className="font-heading text-lg font-semibold text-foreground">Summary</h3>
              <span className="font-semibold text-primary">
                {hasRangePricing ? `From ${formatRequestAmount(subtotal)}` : formatRequestAmount(subtotal)}
              </span>
            </div>
            <RequestItemGroups items={items} compact showImages />
          </section>

          {company?.requiresPayment && !hasRangePricing && (
            <section className="rounded-xl bg-muted/30 p-4">
              <h3 className="font-heading text-base font-semibold text-foreground">Payment due after submission</h3>
              {isQuoteLoading ? (
                <div className="mt-3 h-24 animate-pulse rounded-lg bg-muted" />
              ) : paymentQuote ? (
                <div className="mt-3 space-y-2 text-sm font-caption">
                  <div className="flex justify-between gap-4 text-muted-foreground">
                    <span>Request total</span>
                    <span>GH¢{Number(paymentQuote.sourceTotal ?? subtotal).toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between gap-4 text-foreground">
                    <span>{String(paymentQuote.paymentKind).toLowerCase() === 'deposit' ? `Deposit (${Number(paymentQuote.upfrontPaymentPercentage) || 0}%)` : 'Amount due now'}</span>
                    <span>GH¢{Number(paymentQuote.subtotal).toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between gap-4 text-muted-foreground">
                    <span>Processing fee</span>
                    <span>GH¢{getDisplayedProcessingFee(paymentQuote).toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between gap-4 border-t border-border pt-2 font-semibold text-foreground">
                    <span>Pay now</span>
                    <span className="text-primary">GH¢{Number(paymentQuote.total).toFixed(2)}</span>
                  </div>
                  {Number(paymentQuote.balanceAfterPayment) > 0 && (
                    <div className="flex justify-between gap-4 text-xs text-muted-foreground">
                      <span>Balance after payment</span>
                      <span>GH¢{Number(paymentQuote.balanceAfterPayment).toFixed(2)}</span>
                    </div>
                  )}
                </div>
              ) : (
                <p className="mt-2 text-xs text-muted-foreground">The secure payment page will show the final backend-calculated charge.</p>
              )}
            </section>
          )}

          {hasRangePricing && (
            <section className="rounded-xl border border-warning/20 bg-warning/10 p-4">
              <div className="flex items-start gap-3">
                <Icon name="BadgeDollarSign" size={19} className="mt-0.5 flex-shrink-0 text-warning" />
                <div>
                  <h3 className="font-heading text-base font-semibold text-foreground">Final price confirmed after review</h3>
                  <p className="mt-1 text-sm text-muted-foreground font-caption">
                    This request includes a price-range service. No payment will be started until the business confirms its final price.
                  </p>
                </div>
              </div>
            </section>
          )}

          {hasServices && (
            <section>
              <h3 className="mb-3 font-heading text-lg font-semibold text-foreground">Preferred Schedule</h3>
              <div className="grid gap-4 sm:grid-cols-2">
                <Input
                  type="date"
                  name="requestedDate"
                  label="Preferred date"
                  value={form.requestedDate}
                  onChange={updateField}
                  min={minDate}
                  required
                />
                <Input
                  type="time"
                  name="requestedTime"
                  label="Preferred time"
                  value={form.requestedTime}
                  onChange={updateField}
                  required
                />
              </div>
              <p className="mt-2 text-xs text-muted-foreground">
                This is a preference. {company?.name || 'The business'} will confirm the final schedule.
              </p>
            </section>
          )}

          <section>
            <h3 className="mb-3 font-heading text-lg font-semibold text-foreground">Customer Details</h3>
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="sm:col-span-2">
                <Input
                  name="clientName"
                  label="Full name"
                  value={form.clientName}
                  onChange={updateField}
                  autoComplete="name"
                  required
                />
              </div>
              <Input
                type="email"
                name="clientEmail"
                label="Email"
                value={form.clientEmail}
                onChange={updateField}
                autoComplete="email"
                required
              />
              <Input
                type="tel"
                name="clientPhone"
                label="Phone"
                value={form.clientPhone}
                onChange={updateField}
                autoComplete="tel"
                required
              />
            </div>
          </section>

          <section>
            <label htmlFor="request-note" className="mb-2 block font-heading text-lg font-semibold text-foreground">
              Notes <span className="font-body text-sm font-normal text-muted-foreground">(optional)</span>
            </label>
            <textarea
              id="request-note"
              name="note"
              rows={4}
              value={form.note}
              onChange={updateField}
              placeholder="Describe customisation, product use, sizing, or anything the business should know."
              className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm text-foreground ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
            />
          </section>

          {error && (
            <div role="alert" className="rounded-lg border border-destructive/30 bg-destructive/10 p-3 text-sm text-destructive">
              {error}
            </div>
          )}

          <div className="border-t border-border pt-4">
            <Button type="submit" fullWidth loading={isSubmitting} disabled={items.length === 0 || isQuoteLoading}>
              {isSubmitting
                ? 'Submitting Request...'
                : company?.requiresPayment && paymentQuote && !hasRangePricing
                  ? `Submit & Pay GH¢${Number(paymentQuote.total).toFixed(2)}`
                  : 'Submit Request'}
            </Button>
          </div>
        </form>
      )}
    </Modal>
  );
};

export default RequestFormModal;
